import { inject, Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { IExam, IExamList } from '@tsin-core/models/exam.model';
import { ExamService } from '@tsin-core/services/http/exam.service';
import { AddExam, DeleteExam, GetAllNotRegistered, GetExamById, GetExamGuests, GetExams, UpdateExam, UpdateExamStatus } from './exams.actions';
import { catchError, tap } from 'rxjs';
import { LoadingState } from '@tsin-core/entities';

export interface ExamStateModel {
  exams: IExamList[];
  singleExam: IExamList | null;
  guest: any[];
  notRegistered: any[];
  loading: LoadingState;
}

@State<ExamStateModel>({
  name: 'examState',
  defaults: {
    exams: [],
    singleExam: null,
    loading: LoadingState.notLoading,
    guest: [],
    notRegistered: [],
  },
})
@Injectable()
export class ExamState {
  examService: ExamService = inject(ExamService);

  @Selector()
  static listExams(state: ExamStateModel): IExamList[] {
    return state?.exams ?? [];
  }

  @Selector()
  static getSingleExam(state: ExamStateModel): IExamList | null {
    return state?.singleExam ?? null;
  }

  @Selector()
  static getLoading(state: ExamStateModel): LoadingState {
    return state.loading;
  }

  @Action(GetExams)
  getExams(ctx: StateContext<ExamStateModel>, action: GetExams) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.examService.getExams(action.examType, action.time).pipe(
      tap((exams: { data: IExamList[] }) => {
        ctx.patchState({ exams: exams.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetExamById)
  getSingleExam(ctx: StateContext<ExamStateModel>, action: GetExamById) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.examService.getSingleExam(action.examId).pipe(
      tap((exams: { data: IExamList }) => {
        ctx.patchState({ singleExam: exams.data, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }



  @Action(AddExam)
  addExam(ctx: StateContext<ExamStateModel>, action: AddExam) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.examService.addExam(action.payload).pipe(
      tap((newExam: IExamList) => {
        const state = ctx.getState();
        ctx.patchState({
          exams: [...state.exams, newExam],
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateExam)
  updateExam(ctx: StateContext<ExamStateModel>, action: UpdateExam) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.examService.updateExam(action.payload).pipe(
      tap((newExam: IExamList) => {
        const state = ctx.getState();
        ctx.patchState({
          exams: [...state.exams, newExam],
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(UpdateExamStatus)
  updateExamStatus(ctx: StateContext<ExamStateModel>, action: UpdateExamStatus) {
    ctx.patchState({ loading: LoadingState.loadingAddUpdate });
    return this.examService.updateExamStatus(action.id).pipe(
      tap((response: { data: boolean; }) => {
        const state = ctx.getState();
        ctx.patchState({
          exams: state.exams.map((exam: IExamList) =>
            exam.id === action.id ? { ...exam, isActive: response.data } : exam
          ),
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(DeleteExam)
  deleteExam(ctx: StateContext<ExamStateModel>, action: DeleteExam) {
    ctx.patchState({ loading: LoadingState.loadingDelete });

    return this.examService.deleteExam(action.id).pipe(
      tap(() => {
        const state = ctx.getState();
        ctx.patchState({
          exams: state.exams.filter((exam) => exam.id !== action.id),
          loading: LoadingState.notLoading,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetExamGuests)
  getExamGuests(ctx: StateContext<ExamStateModel>, action: GetExamGuests) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.examService.getExamGuests(action.type).pipe(
      tap((guests) => {
        ctx.patchState({ guest: guests, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }

  @Action(GetAllNotRegistered)
  getAllNotRegistered(ctx: StateContext<ExamStateModel>) {
    ctx.patchState({ loading: LoadingState.loadingList });
    return this.examService.getAllNotRegistered().pipe(
      tap((notRegistered) => {
        ctx.patchState({ notRegistered, loading: LoadingState.notLoading });
      }),
      catchError((error) => {
        ctx.patchState({ loading: LoadingState.notLoading });
        throw error;
      })
    );
  }
}
